import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { clampRange } from '../lib/datalog';
import type {
  GraphChannelSlot,
  GraphPanel,
  LogSessionMetadata,
  TimeRange,
  WorkspaceSnapshot,
} from '../types';

const SLOT_COLORS = ['#ff3b30', '#3b82f6', '#a855f7', '#14b8a6'] as const;

let persistenceTimer: ReturnType<typeof setTimeout> | undefined;
const throttledStorage: StateStorage = {
  getItem: (name) => localStorage.getItem(name),
  removeItem: (name) => localStorage.removeItem(name),
  setItem: (name, value) => {
    clearTimeout(persistenceTimer);
    persistenceTimer = setTimeout(() => localStorage.setItem(name, value), 250);
  },
};

function id(prefix: string): string {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`}`;
}

export function createEmptyGraph(order = 0): GraphPanel {
  const slots = SLOT_COLORS.map(
    (color, slotIndex): GraphChannelSlot => ({
      slotIndex: slotIndex as 0 | 1 | 2 | 3,
      channelId: null,
      enabled: false,
      color,
    }),
  ) as GraphPanel['slots'];
  return { id: id('graph'), order, slots, autoScale: true };
}

function cloneGraphs(graphs: GraphPanel[]): GraphPanel[] {
  return graphs.map((graph) => ({
    ...graph,
    slots: graph.slots.map((slot) => ({ ...slot })) as GraphPanel['slots'],
  }));
}

function createInitialGraphs(session: LogSessionMetadata): GraphPanel[] {
  const normalizedName = (name: string) => name.trim().toLowerCase();
  const findChannel = (name: string) =>
    session.channels.find((channel) => normalizedName(channel.displayName) === name);
  const fillGraph = (order: number, names: string[]) => {
    const graph = createEmptyGraph(order);
    names.forEach((name, slotIndex) => {
      const channel = findChannel(name);
      if (channel) graph.slots[slotIndex] = { ...graph.slots[slotIndex], channelId: channel.id, enabled: true };
    });
    return graph;
  };
  return [
    fillGraph(0, ['engine rpm', 'vehicle speed']),
    fillGraph(1, ['engine oil temp', 'engine coolant temp']),
  ];
}

type WorkspaceValues = {
  graphs: GraphPanel[];
  visibleTimeRange: TimeRange;
  cursorTime: number | null;
  focusedGraphId: string | null;
};

type WorkspaceState = WorkspaceValues & {
  session: LogSessionMetadata | null;
  savedWorkspaces: Record<string, WorkspaceSnapshot>;
  undoStack: WorkspaceSnapshot[];
  inspectorOpen: boolean;
  openSession: (session: LogSessionMetadata) => void;
  closeSession: () => void;
  addGraph: () => void;
  removeGraph: (graphId: string) => void;
  focusGraph: (graphId: string) => void;
  assignChannel: (graphId: string, channelId: string | null, slotIndex?: number) => void;
  setAutoScale: (graphId: string, autoScale: boolean) => void;
  setVisibleTimeRange: (range: TimeRange) => void;
  setCursorTime: (time: number | null) => void;
  setInspectorOpen: (open: boolean) => void;
  undo: () => void;
  resetWorkspace: () => void;
  exportWorkspace: () => WorkspaceSnapshot | null;
};

function snapshot(state: WorkspaceState | (WorkspaceValues & { session: LogSessionMetadata })): WorkspaceSnapshot {
  const session = state.session;
  if (!session) throw new Error('Cannot snapshot a workspace without a session.');
  return {
    schemaVersion: 1,
    fingerprint: session.fingerprint,
    fileName: session.fileName,
    savedAt: Date.now(),
    graphs: cloneGraphs(state.graphs),
    visibleTimeRange: { ...state.visibleTimeRange },
    cursorTime: state.cursorTime,
    focusedGraphId: state.focusedGraphId,
  };
}

function commit(
  state: WorkspaceState,
  changes: Partial<WorkspaceValues>,
  options: { undo?: boolean } = {},
): Partial<WorkspaceState> {
  const next = { ...state, ...changes } as WorkspaceState;
  if (!state.session) return changes;
  const currentSnapshot = snapshot(next);
  return {
    ...changes,
    savedWorkspaces: {
      ...state.savedWorkspaces,
      [state.session.fingerprint]: currentSnapshot,
    },
    undoStack: options.undo
      ? [...state.undoStack, snapshot(state)].slice(-20)
      : state.undoStack,
  };
}

const emptyValues: WorkspaceValues = {
  graphs: [],
  visibleTimeRange: { start: 0, end: 0 },
  cursorTime: null,
  focusedGraphId: null,
};

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      ...emptyValues,
      session: null,
      savedWorkspaces: {},
      undoStack: [],
      inspectorOpen: false,

      openSession: (session) =>
        set((state) => {
          const saved = state.savedWorkspaces[session.fingerprint];
          const channelIds = new Set(session.channels.map((channel) => channel.id));
          const bounds = { start: session.startTime, end: session.endTime };
          if (saved) {
            const graphs = saved.graphs.map((graph, order) => ({
              ...graph,
              order,
              slots: graph.slots.map((slot) =>
                slot.channelId && !channelIds.has(slot.channelId)
                  ? { ...slot, channelId: null, enabled: false }
                  : { ...slot },
              ) as GraphPanel['slots'],
            }));
            const fallbackGraph = graphs[0] ?? createEmptyGraph();
            return {
              session,
              graphs: graphs.length ? graphs : [fallbackGraph],
              visibleTimeRange: clampRange(saved.visibleTimeRange, bounds),
              cursorTime: saved.cursorTime,
              focusedGraphId: graphs.some((graph) => graph.id === saved.focusedGraphId)
                ? saved.focusedGraphId
                : fallbackGraph.id,
              undoStack: [],
              inspectorOpen: false,
            };
          }
          const graphs = createInitialGraphs(session);
          const values: WorkspaceValues = {
            graphs,
            visibleTimeRange: bounds,
            cursorTime: null,
                      focusedGraphId: graphs[0].id,
          };
          const initial = snapshot({ ...values, session });
          return {
            session,
            ...values,
            undoStack: [],
            inspectorOpen: false,
            savedWorkspaces: { ...state.savedWorkspaces, [session.fingerprint]: initial },
          };
        }),

      closeSession: () => set({ ...emptyValues, session: null, undoStack: [], inspectorOpen: false }),

      addGraph: () =>
        set((state) => {
          const graph = createEmptyGraph(state.graphs.length);
          return commit(
            state,
            { graphs: [...state.graphs, graph], focusedGraphId: graph.id },
            { undo: true },
          );
        }),

      removeGraph: (graphId) =>
        set((state) => {
          let graphs = state.graphs.filter((graph) => graph.id !== graphId);
          if (!graphs.length) graphs = [createEmptyGraph()];
          graphs = graphs.map((graph, order) => ({ ...graph, order }));
          const focusedGraphId = state.focusedGraphId === graphId ? graphs[0].id : state.focusedGraphId;
          return commit(state, { graphs, focusedGraphId }, { undo: true });
        }),

      focusGraph: (graphId) => set((state) => commit(state, { focusedGraphId: graphId })),

      assignChannel: (graphId, channelId, slotIndex) =>
        set((state) => {
          const target = state.graphs.find((graph) => graph.id === graphId);
          if (!target) return state;
          if (channelId && target.slots.some((slot, index) => slot.channelId === channelId && index !== slotIndex)) {
            return state;
          }
          const resolvedSlot = slotIndex ?? target.slots.findIndex((slot) => !slot.channelId);
          if (resolvedSlot < 0 || resolvedSlot > 3) return state;
          const graphs = state.graphs.map((graph) => {
            if (graph.id !== graphId) return graph;
            return {
              ...graph,
              slots: graph.slots.map((slot, index) =>
                index === resolvedSlot
                  ? { ...slot, channelId, enabled: Boolean(channelId) }
                  : slot,
              ) as GraphPanel['slots'],
            };
          });
          return commit(
            state,
            { graphs, focusedGraphId: channelId ? graphId : state.focusedGraphId },
            { undo: true },
          );
        }),

      setAutoScale: (graphId, autoScale) =>
        set((state) => {
          const graphs = state.graphs.map((graph) => (graph.id === graphId ? { ...graph, autoScale } : graph));
          return commit(state, { graphs }, { undo: true });
        }),

      setVisibleTimeRange: (range) =>
        set((state) => {
          if (!state.session) return state;
          return commit(state, {
            visibleTimeRange: clampRange(range, {
              start: state.session.startTime,
              end: state.session.endTime,
            }),
          });
        }),

      setCursorTime: (cursorTime) => set({ cursorTime }),
      setInspectorOpen: (inspectorOpen) => set({ inspectorOpen }),

      undo: () =>
        set((state) => {
          const previous = state.undoStack.at(-1);
          if (!previous || !state.session) return state;
          const undoStack = state.undoStack.slice(0, -1);
          return {
            graphs: cloneGraphs(previous.graphs),
            visibleTimeRange: { ...previous.visibleTimeRange },
            cursorTime: previous.cursorTime,
            focusedGraphId: previous.focusedGraphId,
            undoStack,
            savedWorkspaces: {
              ...state.savedWorkspaces,
              [state.session.fingerprint]: { ...previous, savedAt: Date.now() },
            },
          };
        }),

      resetWorkspace: () =>
        set((state) => {
          if (!state.session) return state;
          const graph = createEmptyGraph();
          return commit(
            state,
            {
              graphs: [graph],
              visibleTimeRange: { start: state.session.startTime, end: state.session.endTime },
              cursorTime: null,
                          focusedGraphId: graph.id,
            },
            { undo: true },
          );
        }),

      exportWorkspace: () => {
        const state = get();
        return state.session ? snapshot(state) : null;
      },
    }),
    {
      name: 'idash-datalog-workspaces',
      version: 1,
      storage: createJSONStorage(() => throttledStorage),
      partialize: (state) => ({ savedWorkspaces: state.savedWorkspaces }),
    },
  ),
);

export function enabledChannelIds(graphs: GraphPanel[]): string[] {
  return [...new Set(graphs.flatMap((graph) => graph.slots.map((slot) => slot.channelId)))]
    .filter((id): id is string => Boolean(id));
}
